import { type Market, type Prediction } from '@/common/predictionMarkets/types.ts';
import localDb from '@/common/localDb.ts';
import { parseUnits } from '@/common/helpers';
import playBalance, { balanceRefresh } from '@/common/playAccount/playBalance.ts';
import { calculateShares, loadConfig } from '@/common/predictionMarkets/shares.ts';

export class PlayPredictionMarketProvider {
  constructor() {}

  async makePrediction(market: Market, outcomeId: string, amount: string, userId: string): Promise<boolean> {
    // Check that the user has enough balance to make the prediction
    const balance = await playBalance.getBalance();
    if (balance < BigInt(amount)) {
      throw new Error('Insufficient balance to make prediction');
    }

    const db = await localDb.openDb();
    const tx = db.transaction(localDb.STORE_PLAY_PREDICTIONS, 'readwrite');
    const store = tx.objectStore(localDb.STORE_PLAY_PREDICTIONS);

    const now = Date.now();

    await loadConfig();
    const shares = calculateShares(market, outcomeId, amount);

    const prediction: Prediction = {
      id: `${crypto.randomUUID()}-play`,
      marketId: market.id,
      userId,
      outcomeId,
      amount: Number(amount),
      shares,
      createdAt: now,
    };

    store.put(prediction);

    await new Promise((res, rej) => {
      tx.oncomplete = res;
      tx.onerror = rej;
    });

    db.close();

    await playBalance.decreaseBalance(parseUnits(amount, 18));

    // Trigger balance refresh to update UI
    balanceRefresh.triggerRefresh();

    return true;
  }

  async getUserPredictions(): Promise<Prediction[]> {
    const db = await localDb.openDb();

    return new Promise<Prediction[]>((resolve, reject) => {
      const tx = db.transaction(localDb.STORE_PLAY_PREDICTIONS, 'readonly');
      const store = tx.objectStore(localDb.STORE_PLAY_PREDICTIONS);

      const request = store.getAll();

      request.onsuccess = () => {
        resolve(request.result as Prediction[]);
      };

      request.onerror = () => {
        reject(request.error);
      };

      tx.oncomplete = () => {
        db.close();
      };
    });
  }

  async getUserPredictionsCount(): Promise<number> {
    const predictions = await this.getUserPredictions();
    return predictions.length;
  }

  async getTokenBalanceAndAllowance(): Promise<{ balance: bigint; allowance: bigint; nativeBalance: bigint }> {
    const balance = await playBalance.getBalance();

    return {
      balance,
      allowance: balance,
      nativeBalance: balance,
    };
  }
}

export default PlayPredictionMarketProvider;
